import React from 'react';
import { Link } from 'react-router-dom';

export interface BreadcrumbItem {
  /** 面包屑展示文案或 JSX 元素 */
  label: React.ReactNode;
  /** 跳转链接，若不传则渲染为非链接状态 */
  href?: string;
}

export interface BreadcrumbProps {
  /** 面包屑节点数据列表 */
  items: BreadcrumbItem[];
  /** 自定义 class 名 */
  className?: string;
  /** 右侧追加的扩展元素 (例如收藏按钮、自动保存状态等) */
  extra?: React.ReactNode;
  /** 自定义分隔符元素，默认使用右箭头图标 */
  separator?: React.ReactNode;
}

/**
 * 统一面包屑导航组件 (Breadcrumb)
 * 
 * 规范:
 * 1. 默认应用 pl-1 sm:pl-2 形成良好的内容对齐
 * 2. 纯字无背景、无下边框、无阴影设计
 * 3. 最后一项高亮并支持自动截断
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  className = '',
  extra,
  separator = <i className="ri-arrow-right-s-line text-foreground-300 text-xs shrink-0"></i>,
}) => {
  if (!items || items.length === 0) return null;

  return (
    <div className={`flex items-center justify-between gap-4 py-3 sm:py-3.5 pl-1 sm:pl-2 ${className}`}>
      <nav className="flex items-center gap-2 text-xs sm:text-sm text-foreground-500 min-w-0 flex-1 flex-wrap">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <React.Fragment key={idx}>
              {idx > 0 && <span className="flex items-center shrink-0">{separator}</span>}
              {isLast ? (
                <span className="text-foreground-800 font-medium truncate max-w-[200px] sm:max-w-md md:max-w-lg">
                  {item.label}
                </span>
              ) : item.href ? (
                <Link
                  to={item.href}
                  className="hover:text-primary-600 transition-colors cursor-pointer shrink-0"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-foreground-600 shrink-0">{item.label}</span>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {extra && <div className="shrink-0 flex items-center">{extra}</div>}
    </div>
  );
};

export default Breadcrumb;
